import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Sparkles, Lock, Feather, BookOpen, Compass, Mountain, Repeat,
  UserCircle, Leaf, Link2, Clock, Smartphone, Wind, Droplet, Heart,
  Sprout, Shield, EyeOff, TrendingUp, Target, Flame, Brain, Users,
  Anchor, Sunrise, Star, ArrowRight, X,
  ScrollText, GaugeCircle, KeyRound,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import PreviewChip, { PreviewIntroBanner } from "../components/PreviewChip";
import ParchmentBackground from "../components/ParchmentBackground";
import DemoCraftPrintCard from "../components/mycraftz/DemoCraftPrintCard";
import { loadPreviewInstalled } from "../data/previewSample";
import { bookIdForSlug } from "../data/mycraftzShowcase";
import { bookExamples } from "../data/bookExamples";
import { getJourney, patchJourney, isPreviewMode } from "../lib/previewJourney";
import "../styles/MyCraftzPage.css";
import { GraphPrintzDashboard } from "../components/mycraftz/GraphPrintzDashboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  Mountain, Repeat, UserCircle, Leaf, Link: Link2, Clock, Smartphone, Wind,
  Droplet, Heart, Sprout, Shield, EyeOff, TrendingUp, Compass, Target, Flame,
  Brain, Users, Anchor, Sunrise, Star, BookOpen,
};

// Book-cover source for Active CraftPrint cards. Maps showcase book_slug →
// ISBN-13 and reuses the same Open Library covers API that /library and the
// Book Detail pages already use. Small (S) size gives us the ~80-120px
// thumbnail scale used inside the card (Open Library sizes: S / M / L).
const BOOK_ISBN_BY_SLUG = {
  "atomic-habits":          "9780735211292",
  "deep-work":              "9781455586691",
  "psychology-of-money":    "9780857197689",
  "think-and-grow-rich":    "9781585424337",
  "mans-search-for-meaning":"9780807014295",
};
function coverUrlForSlug(slug, size = "M") {
  const isbn = BOOK_ISBN_BY_SLUG[slug];
  return isbn ? `https://covers.openlibrary.org/b/isbn/${isbn}-${size}.jpg` : null;
}


function Icon({ name, size = 18 }) {
  const C = ICONS[name] || Sparkles;
  return <C size={size} />;
}

/* ──────────────────────────────────────────────────────────
   GraphPrintz — brass gauge in vintage manuscript style.
   Uses a hand-drawn-looking SVG arc, NOT a modern progress bar.
   ────────────────────────────────────────────────────────── */
function ShowcaseGrid({ showcase, onPick, journeyBySlug }) {
  return (
    <div className="mc-showcase-grid" data-testid="mc-showcase-grid">
      {showcase.map((b) => {
        const label = journeyBySlug?.[b.slug];
        return (
          <button
            key={b.slug}
            type="button"
            className={`mc-showcase-card ${label ? "is-installed" : ""}`}
            data-testid={`mc-showcase-${b.slug}`}
            onClick={() => onPick(b.slug)}
          >
            <span className="mc-showcase-ribbon">CraftPrint™</span>
            <div className="mc-showcase-meta">
              <p className="mc-showcase-cat">{b.category}</p>
              <h3 className="mc-showcase-title">{b.title}</h3>
              <p className="mc-showcase-author">by {b.author}</p>
            </div>
            <div className="mc-showcase-foot">
              <span className="mc-showcase-rating">★ {b.rating}</span>
              <span className="mc-showcase-cta" data-testid={`mc-showcase-cta-${b.slug}`}>
                {label || "Read the CraftPrint"} <ArrowRight size={14} />
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────
   Install modal — shown on showcase card click.
   ────────────────────────────────────────────────────────── */
function InstallCraftPrintModal({ slug, onClose, onInstalled, previewMode, picks }) {
  const { isAuthenticated, authHeaders } = useAuth();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${API}/mycraftz/showcase/${slug}`)
      .then((res) => { if (alive) setBook(res.data); })
      .catch(() => { if (alive) setError("Could not load this CraftPrint."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  const cp = book?.craftprint || null;

  const handleInstall = async () => {
    // Preview mode: never persist server-side. Advance the journey state and
    // continue automatically into Practice · Sadhana (D22 / continuous flow).
    if (previewMode) {
      patchJourney({ stage: "sadhana", book: slug });
      onClose();
      // Route through the proven bridge path (numeric id + bridge=1) so the
      // real Sadhana auto-seeds the committed ideas and renders practices.
      const bParam = bookIdForSlug(slug) ?? slug;
      navigate(`/sadhana?book=${bParam}&bridge=1`);
      return;
    }
    // One system: Install continues into the real Bodha → Sankalpa → Sadhana
    // journey for this book (the showcase idea arrives pre-marked "resonated").
    const target = `/bodha?book=${bookIdForSlug(slug) ?? slug}&showcase=${slug}`;
    onClose();
    if (!isAuthenticated) {
      navigate("/login", { state: { from: target } });
      return;
    }
    navigate(target);
  };

  // In Preview mode, "close" should not create a dead end. Send the visitor
  // back to Sankalpa with their picks intact so they can re-choose. (D22)
  const handlePreviewBack = () => {
    onClose();
    const bParam = bookIdForSlug(slug) ?? slug;
    navigate(`/sankalpa?book=${bParam}`);
  };

  return (
    <motion.div
      className="mc-modal-overlay"
      data-testid="mc-install-modal"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="mc-modal mc-install-modal"
        onClick={(e) => e.stopPropagation()}
        initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 10, opacity: 0 }}
        transition={{ duration: 0.28 }}
      >
        <button
          type="button"
          className="mc-modal-close"
          onClick={previewMode ? handlePreviewBack : onClose}
          aria-label={previewMode ? "Back to Sankalpa" : "Close"}
          data-testid="mc-install-close"
        >
          <X size={18} />
        </button>

        {loading || !book ? (
          <div className="mc-modal-loading">Unrolling the scroll…</div>
        ) : (
          <>
            <p className="mc-modal-eyebrow">From <em>{book.title}</em> · {book.author}</p>
            <h2 className="mc-modal-title">{cp.idea_name}</h2>
            <p className="mc-modal-sutra">"{cp.sutra}"</p>

            {previewMode && Array.isArray(picks) && picks.length > 0 ? (
              <div
                data-testid="mc-picks-summary"
                style={{
                  margin: "6px 0 14px",
                  padding: "10px 14px",
                  borderRadius: 8,
                  background: "rgba(212,171,86,0.14)",
                  border: "1px solid rgba(139,101,32,0.35)",
                  color: "#3a2611",
                  fontFamily: "'Cormorant Garamond',Georgia,serif",
                  fontSize: "0.95rem",
                  lineHeight: 1.55,
                }}
              >
                <strong style={{ letterSpacing: "0.04em" }}>
                  You chose {picks.length} Craft Idea{picks.length > 1 ? "s" : ""}.
                </strong>{" "}
                Below is the CraftPrint the AI recommends based on your commitment.
                In Preview mode the recommended blueprint reflects the book's showcase content — a full multi-idea CraftPrint will render for subscribers.
              </div>
            ) : null}

            <div className="mc-modal-block">
              <h4>Why this idea</h4>
              <p>{cp.why_this_idea}</p>
            </div>

            <ul className="mc-blueprint">
              <li>
                <span className="mc-bp-label">{cp.trigger_label} · Trigger</span>
                <input
                  className="mc-bp-input"
                  defaultValue={cp.trigger}
                  readOnly
                  data-testid="mc-install-trigger"
                />
              </li>
              <li>
                <span className="mc-bp-label">{cp.action_label} · Action</span>
                <input
                  className="mc-bp-input"
                  defaultValue={cp.action}
                  readOnly
                  data-testid="mc-install-action"
                />
              </li>
              <li>
                <span className="mc-bp-label">{cp.duration_label} · Duration (days)</span>
                <input
                  className="mc-bp-input mc-bp-input-small"
                  type="number" min={1} max={365}
                  defaultValue={cp.duration_days}
                  readOnly
                  data-testid="mc-install-duration"
                />
              </li>
              <li className="mc-bp-readonly">
                <span className="mc-bp-label">{cp.measure_label} · Measure</span>
                <span className="mc-bp-value">{cp.measure}</span>
              </li>
              <li className="mc-bp-readonly">
                <span className="mc-bp-label">{cp.reward_label} · Reward</span>
                <span className="mc-bp-value">{cp.reward}</span>
              </li>
              <li className="mc-bp-readonly">
                <span className="mc-bp-label">{cp.obstacle_label} · Obstacle</span>
                <span className="mc-bp-value">{cp.obstacle}</span>
              </li>
              <li className="mc-bp-readonly">
                <span className="mc-bp-label">{cp.antidote_label} · Antidote</span>
                <span className="mc-bp-value">{cp.antidote}</span>
              </li>
            </ul>

            <p className="mc-modal-insight">{cp.transformation_insight}</p>
            {error ? <p className="mc-modal-error" data-testid="mc-install-error">{error}</p> : null}

            <div className="mc-modal-actions">
              {previewMode ? (
                <button
                  type="button"
                  className="mc-btn-primary mc-btn-lg"
                  onClick={handleInstall}
                  data-testid="mc-install-preview-continue"
                >
                  Accept CraftPrint &amp; Begin Sadhana →
                </button>
              ) : !isAuthenticated ? (
                <button
                  type="button"
                  className="mc-btn-primary mc-btn-lg"
                  onClick={handleInstall}
                  data-testid="mc-install-signin"
                >
                  <KeyRound size={14} /> Sign in to install this CraftPrint
                </button>
              ) : (
                <button
                  type="button"
                  className="mc-btn-primary mc-btn-lg"
                  onClick={handleInstall}
                  disabled={submitting}
                  data-testid="mc-install-confirm"
                >
                  {submitting ? "Sealing…" : "Install Into MyCraftz™"}
                </button>
              )}
              {previewMode ? (
                <button
                  type="button"
                  className="mc-btn-ghost"
                  onClick={handlePreviewBack}
                  data-testid="mc-install-preview-back"
                >
                  ← Change my picks (back to Sankalpa)
                </button>
              ) : (
                <button type="button" className="mc-btn-ghost" onClick={onClose}>
                  Not yet — keep browsing
                </button>
              )}
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

/* ──────────────────────────────────────────────────────────
   Scroll-view modal — open a single installed CraftPrint with reflections
   ────────────────────────────────────────────────────────── */
function ScrollModal({ item, onClose }) {
  const cp = item.craftprint || {};
  return (
    <motion.div className="mc-modal-overlay" data-testid="mc-scroll-modal"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div className="mc-modal mc-scroll-modal" onClick={(e) => e.stopPropagation()}
        initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 10, opacity: 0 }}>
        <button type="button" className="mc-modal-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
        <p className="mc-modal-eyebrow">From <em>{item.book_title}</em></p>
        <h2 className="mc-modal-title">{item.idea_name}</h2>
        <p className="mc-modal-sutra">"{cp.sutra}"</p>

        <ul className="mc-blueprint">
          <li className="mc-bp-readonly"><span className="mc-bp-label">{cp.trigger_label}</span><span className="mc-bp-value">{cp.trigger}</span></li>
          <li className="mc-bp-readonly"><span className="mc-bp-label">{cp.action_label}</span><span className="mc-bp-value">{cp.action}</span></li>
          <li className="mc-bp-readonly"><span className="mc-bp-label">{cp.duration_label}</span><span className="mc-bp-value">{item.duration_days} days</span></li>
          <li className="mc-bp-readonly"><span className="mc-bp-label">{cp.measure_label}</span><span className="mc-bp-value">{cp.measure}</span></li>
          <li className="mc-bp-readonly"><span className="mc-bp-label">{cp.reward_label}</span><span className="mc-bp-value">{cp.reward}</span></li>
          <li className="mc-bp-readonly"><span className="mc-bp-label">{cp.obstacle_label}</span><span className="mc-bp-value">{cp.obstacle}</span></li>
          <li className="mc-bp-readonly"><span className="mc-bp-label">{cp.antidote_label}</span><span className="mc-bp-value">{cp.antidote}</span></li>
        </ul>

        <div className="mc-scroll-reflections">
          <h4>Reflections</h4>
          {(item.reflections || []).length === 0 ? (
            <p className="mc-empty-line">Nothing written yet. The scroll waits.</p>
          ) : (
            <ul>
              {(item.reflections || []).slice().reverse().map((r) => (
                <li key={r.id}>
                  <time>{new Date(r.at).toLocaleDateString()}</time>
                  <p>{r.text}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ──────────────────────────────────────────────────────────
   Real CraftPrint card — a book the user has sealed a Sankalpa on
   via the real Library → Bodha → Sankalpa → Sadhana flow.
   One card per book, aggregate 30-day progress.
   ────────────────────────────────────────────────────────── */
// Book cover with a parchment placeholder until the real image has loaded
// (and kept if it fails or comes back as a 1×1 "no cover" pixel).




/* ──────────────────────────────────────────────────────────
   PAGE
   ────────────────────────────────────────────────────────── */
export default function MyCraftzPage() {
  const { isAuthenticated, authHeaders, user, loading: authLoading } = useAuth();
  const [showcase, setShowcase] = useState([]);
  const [installed, setInstalled] = useState([]);
  const [myCraftprints, setMyCraftprints] = useState([]);
  const [stats, setStats] = useState(null); // real summary from /my-craftprints
  const [pickSlug, setPickSlug] = useState(null);
  const [scrollItem, setScrollItem] = useState(null);
  const [loadingInstalled, setLoadingInstalled] = useState(false);

  // ── Preview mode: parse ?mode=preview off the URL and load sample data.
  //     Also fall back to persisted journey state so browser back/refresh
  //     inside the Preview Journey never boots the visitor out of Preview.
  const location = useLocation();
  // Option A: real-vs-sample is decided by auth state, NOT by leftover
  // icraftz.preview.journey.v1 in localStorage. A logged-in user (or one whose
  // auth is still resolving) only ever sees the sample/demo workspace when the
  // URL explicitly asks for it via ?mode=preview. Logged-out visitors keep the
  // prior behaviour (explicit param OR a saved journey => preview).
  const previewMode = useMemo(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("mode") === "preview") return true;
    if (isAuthenticated || authLoading) return false;
    return isPreviewMode(location.search);
  }, [location.search, isAuthenticated, authLoading]);
  const currentPicks = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const fromUrl = params.get("picks");
    if (fromUrl) return fromUrl.split(",").filter(Boolean);
    const j = getJourney();
    return (j && Array.isArray(j.picks)) ? j.picks : [];
  }, [location.search]);
  const [previewLoaded, setPreviewLoaded] = useState(false);

  useEffect(() => {
    if (!previewMode) return;
    let alive = true;
    loadPreviewInstalled(API)
      .then((rows) => {
        if (alive) {
          setInstalled(rows);
          setPreviewLoaded(true);
        }
      })
      .catch(() => { if (alive) setPreviewLoaded(true); });
    return () => { alive = false; };
  }, [previewMode]);

  // When we land on the workspace (mode=preview, no install param), the
  // journey completes. Record the final stage so state reflects reality.
  useEffect(() => {
    if (!previewMode) return;
    const params = new URLSearchParams(location.search);
    const installing = params.get("install");
    if (!installing) {
      patchJourney({ stage: "workspace" });
    }
  }, [previewMode, location.search]);

  // Showcase is public.
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/mycraftz/showcase`).then((res) => {
      if (alive) setShowcase(res.data?.books || []);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Allow deep-link from book modal: /mycraftz?install=atomic-habits
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const s = params.get("install");
    if (s) setPickSlug(s);
  }, [location.search]);

  const [dashboard, setDashboard] = useState(undefined);
  const reloadInstalled = async () => {
    if (previewMode) return; // preview data is client-side only
    if (!isAuthenticated) { setMyCraftprints([]); setDashboard(undefined); return; }
    setLoadingInstalled(true);
    try {
      const [a, c, d] = await Promise.all([
        axios.get(`${API}/mycraftz/installed`, { headers: authHeaders() }),
        axios.get(`${API}/mycraftz-install/my-craftprints`, { headers: authHeaders() }),
        axios.get(`${API}/mycraftz-install/dashboard`, { headers: authHeaders() }).catch(() => ({ data: { error: true } })),
      ]);
      setInstalled(a.data?.installed || []);
      setStats(c.data?.summary || null);
      setMyCraftprints(c.data?.craftprints || []);
      setDashboard(d.data);
    } catch (err) {
    } finally { setLoadingInstalled(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reloadInstalled(); }, [isAuthenticated, previewMode]);

  // Showcase tile label from the NEW journey only — never "Installed" from legacy data.
  const journeyBySlug = useMemo(() => {
    const out = {};
    (showcase || []).forEach((b) => {
      const j = (dashboard?.journeys || []).find((x) => String(x.book_id) === String(bookIdForSlug(b.slug)) && !x.legacy);
      if (j) out[b.slug] = j.stage === "sadhana" ? "Sadhana in progress" : "In your journey";
    });
    return out;
  }, [dashboard, showcase]);

  // Nidhi: ideas from the real Sadhana flow that reached the 30-day target.
  const completed = myCraftprints.flatMap((cp) =>
    (cp.ideas || []).filter((i) => i.completed).map((i) => ({ ...i, book_id: cp.book_id, book_title: cp.book_title }))
  );

  // Cold-load guard: until auth resolves, don't flash the logged-out
  // showcase to a user who is actually signed in.
  if (authLoading) {
    return (
      <div className="mc-page" data-testid="mycraftz-page">
        <div
          data-testid="mycraftz-auth-loading"
          style={{
            minHeight: "60vh", display: "flex", alignItems: "center",
            justifyContent: "center", fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontStyle: "italic", fontSize: "1.05rem", color: "#5C4A3A",
          }}
        >
          Opening your chamber…
        </div>
      </div>
    );
  }

  return (
    <div className="mc-page" data-testid="mycraftz-page">
      <ParchmentBackground />
      {previewMode ? <PreviewChip stage="Experience" /> : null}

      {/* Hero */}
      <header className="mc-hero">
        <Link to="/" className="mc-back" data-testid="mc-back-home"><ArrowRight size={14} style={{ transform: "rotate(180deg)" }} /> back to homepage</Link>
        <p className="mc-hero-eyebrow">a hidden chamber within iCraftz</p>
        <h1 className="mc-hero-title">MyCraftz<span className="mc-tm">™</span></h1>
        <p className="mc-hero-sub">
          Every meaningful change begins with a single idea.<br />
          This is where the books you've read become the life you live.
        </p>
        <div className="mc-hero-rule" aria-hidden="true">
          <span /> <em>❦</em> <span />
        </div>
      </header>

      {previewMode ? <PreviewIntroBanner /> : null}

      {/* GraphPrintz — hidden in Preview (D25 / B5: no fabricated stats) */}
      {!previewMode && isAuthenticated ? <GraphPrintzDashboard data={dashboard === undefined ? null : dashboard} /> : null}

      {/* Signed-out: demo CraftPrints + sign-in prompt. Signed-in practice lives inside GraphPrintz. */}
      {!isAuthenticated && (
      <section className="mc-section" data-testid="mc-section-active">
        <h2 className="mc-section-title">CraftPrint<span className="mc-tm">™</span></h2>
        <p className="mc-section-sub">The blueprint: three ideas you commit to and practise.</p>

        {!isAuthenticated ? (
          <>
            {/* Anonymous demo — 5 live CraftPrints backed by /craftprint/demo-init.
                Renders for BOTH pure-anonymous visitors AND preview-journey
                visitors (whose localStorage carries icraftz.preview.journey and
                would otherwise trip isPreviewMode()). Auth'd users see their
                real installed CraftPrints via the branches below.

                Edits stored server-side under a per-browser install_id kept in
                localStorage, so refresh + edits survive but never leak between
                browsers. Same reveal cadence a real subscriber sees. */}
            <div
              className="mc-section-demo"
              data-testid="mc-demo-craftprints-section"
              style={{ marginBottom: 28 }}
            >
              <p className="mc-demo-lead">
                <Sparkles size={12} strokeWidth={1.6} /> Try five live CraftPrints — no sign-in
              </p>
              <p className="mc-section-sub" style={{ marginTop: 0, marginBottom: 22 }}>
                Each card is generated by the same pipeline a subscriber runs after Sankalpa.
                Reveal cadence (day 0 · day 3 · day 7) is preserved; toggle{" "}
                <em>Preview reveal</em> on any card to see what unlocks next.
                Your edits are saved in this browser.
              </p>
              <div className="mc-active-grid" data-testid="mc-demo-craftprint-grid">
                {(showcase || []).slice(0, 5).map((b) => (
                  <DemoCraftPrintCard
                    key={b.slug}
                    book={{
                      slug:   b.slug,
                      title:  b.title,
                      author: b.author,
                      cover:  coverUrlForSlug(b.slug),
                    }}
                    lang="en"
                  />
                ))}
              </div>
            </div>

            {/* Subtle sign-in CTA sits BELOW the demo, not above it — the visitor
                gets value first, then the invitation. */}
            <div className="mc-locked-card" data-testid="mc-locked-active">
              <Lock size={18} />
              <p>
                Ready to make it yours?{" "}
                Sign in to install real CraftPrintz<span className="mc-tm">™</span>,
                track streaks, and grow your Nidhi<span className="mc-tm">™</span>.
              </p>
              <div className="mc-locked-actions">
                <Link to="/login" className="mc-btn-primary mc-btn-lg">Sign in</Link>
                <Link to="/signup" className="mc-btn-ghost">Create an account</Link>
              </div>
            </div>
          </>
        ) : null}
      </section>
      )}

      {/* Showcase */}
      <section className="mc-section mc-section-showcase" id="showcase" data-testid="mc-section-showcase">
        <h2 className="mc-section-title">Five Books. Five Installable Ideas.</h2>
        <p className="mc-section-sub">
          These are the showcase CraftPrintz<span className="mc-tm">™</span>. Pick a scroll and read its blueprint —
          you can install it whenever you're ready.
        </p>
        <ShowcaseGrid
          showcase={showcase}
          journeyBySlug={journeyBySlug}
          onPick={(slug) => setPickSlug(slug)}
        />
      </section>

      {/* Nidhi — completed library */}
      {isAuthenticated && completed.length > 0 ? (
        <section className="mc-section" data-testid="mc-section-nidhi">
          <h2 className="mc-section-title">Nidhi<span className="mc-tm">™</span></h2>
          <p className="mc-section-sub">The repository of ideas you've fully installed into your life.</p>
          <ul className="mc-nidhi-grid">
            {completed.map((item) => (
              <li key={`${item.book_id}-${item.idea_id}`} className="mc-nidhi-card" data-testid={`mc-nidhi-${item.book_id}-${item.idea_id}`}>
                <p className="mc-nidhi-book">{item.book_title}</p>
                <h4 className="mc-nidhi-title">{item.title}</h4>
                {item.sutra ? <p className="mc-nidhi-sutra">"{item.sutra}"</p> : null}
                <p className="mc-streak"><Flame size={12} /> {item.days_kept} of {item.target} days kept</p>
                <Link to={`/siddhi?book=${item.book_id}&bridge=1`} className="mc-btn-ghost mc-btn-sm" data-testid={`mc-nidhi-open-${item.book_id}-${item.idea_id}`}>
                  <ScrollText size={12} /> See retrospective
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <footer className="mc-foot" aria-hidden="true">
        <span /> <em>iCraftz · MyCraftz™ · less noise, more thought</em> <span />
      </footer>

      <AnimatePresence>
        {pickSlug ? (
          <InstallCraftPrintModal
            key="install"
            slug={pickSlug}
            previewMode={previewMode}
            picks={currentPicks}
            onClose={() => setPickSlug(null)}
            onInstalled={async () => { await reloadInstalled(); setPickSlug(null); }}
          />
        ) : null}
        {scrollItem ? (
          <ScrollModal key="scroll" item={scrollItem} onClose={() => setScrollItem(null)} />
        ) : null}
      </AnimatePresence>
    </div>
  );
}
